import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { instrumentProfitabilityApi } from '@/api/services/instrumentProfitability';
import { todayDateString, yesterdayDateString } from '@/api/services/riskDashboard';
import {
  InstrumentProfitabilityFilterBar,
  defaultIpFilters,
  toIpInput,
  type IpFilterState,
} from './InstrumentProfitabilityFilterBar';
import { InstrumentProfitabilityTable } from './InstrumentProfitabilityTable';
import { InstrumentProfitabilityPermissions as P } from './permissions';

export function InstrumentProfitabilityPage() {
  const toast = useToast();
  const can = usePermission();
  const [draft, setDraft] = useState<IpFilterState>(defaultIpFilters);
  const [applied, setApplied] = useState<IpFilterState>(draft);
  const [hasFetched, setHasFetched] = useState(false);
  // Bumped on every Fetch click so re-applying the SAME filters still forces a fresh
  // request. react-query compares the key by value, so without this an unchanged filter
  // set is a cache hit and the Fetch click appears to do nothing.
  const [fetchNonce, setFetchNonce] = useState(0);
  // Short-lived click feedback so the Fetch button shows a loading state even on a
  // cache hit (no network request), confirming the click registered.
  const [fetchPulse, setFetchPulse] = useState(false);

  // Legacy requires a Fetch click (no auto-load); the empty table is shown directly.
  const q = useQuery({
    queryKey: ['ip-data', applied, fetchNonce],
    queryFn: () => instrumentProfitabilityApi.getData(toIpInput(applied)),
    enabled: hasFetched,
  });

  // Post-fetch warnings (message + missing rates), like legacy.
  const data = q.data;
  useEffect(() => {
    if (!data) return;
    if (data.message) toast.info(data.message);
    if (data.missingRates && data.missingRates.length) {
      toast.info(`Missing conversion rates for currencies: ${data.missingRates.join(', ')}`);
    }
  }, [data, toast]);

  const fetch = () => {
    if (!draft.startDate || !draft.endDate) {
      toast.error('Please make sure that both start and end date are provided.');
      return;
    }
    setApplied({ ...draft });
    setHasFetched(true);
    // Re-applying identical filters is a react-query cache hit. That's fine for static
    // historical data, but live / today-ending data changes — so only then force a
    // fresh request by bumping the nonce.
    if (draft.isLive || draft.endDate === todayDateString()) setFetchNonce((n) => n + 1);
    setFetchPulse(true);
    window.setTimeout(() => setFetchPulse(false), 450);
  };

  const onModeToggle = (key: 'isLive' | 'isConsolidated' | 'showLp', value: boolean) => {
    if (key === 'isLive') {
      const newEnd = value ? todayDateString() : yesterdayDateString();
      setDraft((f) => ({ ...f, isLive: value, endDate: newEnd, startDate: f.startDate > newEnd ? newEnd : f.startDate }));
      return;
    }
    if (key === 'isConsolidated') {
      // Legacy resets all filters when By-UID is toggled.
      setDraft({ ...defaultIpFilters(), isConsolidated: value });
      return;
    }
    setDraft((f) => ({ ...f, [key]: value }));
  };

  return (
    <div className="instrument-profitability-page">
      <PageHeader title="Instrument Profitability" breadcrumb={['Home', 'Instrument Profitability']} />

      <InstrumentProfitabilityFilterBar
        draft={draft}
        setDraft={setDraft}
        onFetch={fetch}
        onModeToggle={onModeToggle}
        canConsolidated={can(P.consolidatedView)}
        fetching={q.isFetching || fetchPulse}
      />

      <InstrumentProfitabilityTable rows={data?.items ?? []} loading={q.isFetching} />
    </div>
  );
}
